import { lazy, useContext, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import axios from 'axios'

import * as routes from '../routes'
import { Layout, DateRangePicker, AlertContext } from '../widgets'
import {
  Loading,
  useAdminTitle,
} from '../shared'

const AdminCharts = lazy(() => import('./components/AdminCharts'))

const AdminAnalytics = (props) => {
  const [loading, setLoading] = useState(true)
  const [preset, setPreset] = useState('last90')
  const [startDate, setStartDate] = useState(dayjs().utc().subtract(90, 'days'))
  const [endDate, setEndDate] = useState(dayjs().utc().subtract(1, 'days'))
  const [interval, setInterval] = useState('day')
  const [hideOutliers, setHideOutliers] = useState(false)
  const [rawCharts, setRawCharts] = useState([])
  const [charts, setCharts] = useState([])

  useEffect(() => {
    loadData(startDate, endDate)
  }, [])

  useEffect(() => {
    const el = document.querySelector('.content')
    if (el) {
      el.style.maxWidth = 'none'
      return () => { el.style.maxWidth = '' }
    }
  }, [])

  useAdminTitle('Analytics')

  const { handleError } = useContext(AlertContext)

  const aggregateByInterval = (dataPoints, intervalValue) => {
    if (intervalValue === 'day') return dataPoints

    const getGroupKey = (dayStr) => {
      const date = dayjs(dayStr, 'M/D/YY')
      if (intervalValue === 'week') return date.startOf('week').format('M/D/YY')
      return date.format('MMM YYYY')
    }

    const groups = {}
    const order = []

    for (const point of dataPoints) {
      const key = getGroupKey(point.day)
      if (!groups[key]) {
        groups[key] = { day: key }
        order.push(key)
      }
      for (const [k, v] of Object.entries(point)) {
        if (k !== 'day') groups[key][k] = (groups[key][k] || 0) + v
      }
    }

    return order.map(key => groups[key])
  }

  const toLocalChartData = (serverData, start, end, intervalValue) => {
    const localData = []
    for (const chartData of serverData) {
      const dataPointsByDay = chartData.dataPoints.reduce((out, dataPoint) => {
        var day = dataPoint.day
        out[day] = out[day] || []
        out[day].push(dataPoint)
        return out
      }, {})
      const dataPointNames = [...new Set(chartData.dataPoints.map(dataPoint => dataPoint.name))]
      const dataPoints = []

      for (var date = start; date.diff(end.endOf('day')) <= 0; date = date.add(1, 'days')) {
        const day = date.format('M/D/YY')
        const dataPointsOnDay = dataPointsByDay[day] || []
        for (const dataPointName of dataPointNames) {
          if (!dataPointsOnDay.find((dataPoint) => dataPoint.name === dataPointName)) {
            dataPointsOnDay.push({name: dataPointName, day: day, count: 0})
          }
        }

        dataPoints.push(dataPointsOnDay.reduce((out, dataPoint) => {
          out[dataPoint.name] = dataPoint.count
          return out
        }, {day: day}))
      }

      localData.push({
        name: chartData.name,
        type: chartData.type,
        dataPoints: aggregateByInterval(dataPoints, intervalValue),
        dataPointNames: dataPointNames,
      })
    }

    return localData
  }

  const loadData = (start, end) => {
    axios.get(routes.adminAnalyticsUrl, {
      params: {
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD'),
      }
    })
      .then((response) => {
        const raw = response.data
        setRawCharts(raw)
        setCharts(toLocalChartData(raw, start, end, interval))
      }).catch((error) => {
        handleError(error)
      }).finally(() => {
        setLoading(false)
      })
  }

  const onDateChanged = (stateKey, date) => {
    const setter = { startDate: setStartDate, endDate: setEndDate }[stateKey]
    setter(dayjs(date, 'YYYY-MM-DD').utc())
  }

  const onPresetChanged = (newPreset) => {
    if (newPreset === 'custom') {
      setPreset(newPreset)
      return
    }
    const newEnd = dayjs().utc().subtract(1, 'days')
    const newStart = {
      last30: () => dayjs().utc().subtract(30, 'days'),
      last90: () => dayjs().utc().subtract(90, 'days'),
      last12months: () => dayjs().utc().subtract(1, 'year'),
    }[newPreset]()
    setPreset(newPreset)
    setStartDate(newStart)
    setEndDate(newEnd)
    loadData(newStart, newEnd)
  }

  const filterOutliers = (chartData) => {
    return chartData.map(chart => {
      const dataPoints = chart.dataPoints.map(p => ({...p}))
      for (const name of chart.dataPointNames) {
        const observed = dataPoints.map(p => p[name]).filter(v => v > 0)
        if (observed.length < 2) continue
        const mean = observed.reduce((a, b) => a + b, 0) / observed.length
        const stdDev = Math.sqrt(observed.reduce((sum, v) => sum + (v - mean) ** 2, 0) / observed.length)
        if (stdDev === 0) continue
        for (const point of dataPoints) {
          if (point[name] > 0 && Math.abs((point[name] - mean) / stdDev) > 3) point[name] = null
        }
      }
      return {...chart, dataPoints}
    })
  }

  const onIntervalChanged = (newInterval) => {
    setInterval(newInterval)
    setCharts(toLocalChartData(rawCharts, startDate, endDate, newInterval))
  }

  if (loading) {
    return <Loading />
  }

  const body =
    <Layout>
      <Layout.Header>Analytics</Layout.Header>
      <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '16px'}}>
        <select value={interval} onChange={e => onIntervalChanged(e.target.value)}>
          <option value='day'>Day</option>
          <option value='week'>Week</option>
          <option value='month'>Month</option>
        </select>
        <select value={preset} onChange={e => onPresetChanged(e.target.value)}>
          <option value='last30'>Last 30 days</option>
          <option value='last90'>Last 90 days</option>
          <option value='last12months'>Last 12 months</option>
          <option value='custom'>Custom</option>
        </select>
        {preset === 'custom' &&
          <DateRangePicker
            minDate={'2018-10-06'}
            startDate={startDate}
            endDate={endDate}
            onDateChanged={onDateChanged}
            onGo={() => loadData(startDate, endDate)}
          />
        }
      </div>
      <div style={{marginBottom: '16px'}}>
        <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer'}}>
          <input
            type='checkbox'
            checked={hideOutliers}
            onChange={e => setHideOutliers(e.target.checked)}
          />
          Hide Outliers
        </label>
      </div>
      <AdminCharts charts={hideOutliers ? filterOutliers(charts) : charts} />
    </Layout>

  return (
    <div className='body-container'>
      {body}
    </div>
  )
}

export default AdminAnalytics
